import React from "react";
import { Intent, TextArea } from "@blueprintjs/core";
import styles from "./OpportunityDetails.module.scss";

export function getColumnWidth({ columnWidths, index, width }) {
  return columnWidths[index] || (1 / 6) * width;
}

export const onColumnResizeHof = ({
  columnWidths,
  setColumnWidths,
  columns
}) => ({ column, width }) => {
  const index = columns.findIndex((c) => c.key === column.key);
  const newWidths = columnWidths;
  newWidths[index] = width;

  setColumnWidths(newWidths);
};

export const getApplicantsAndInfluencers = ({
  opportunity,
  onApplicationAction,
  onInitChat
}) => {
  if (
    !opportunity ||
    !opportunity.applicants ||
    !opportunity.applicants.length
  ) {
    return {
      applicants: [],
      influencers: []
    };
  }
  const { applicants } = opportunity;

  const influencers = applicants
    .filter(
      (applicant) => applicant.currentApplicationEvent.status === "Approved"
    )
    .map((influencer) => ({
      name: influencer.account.name,
      appliedDate: influencer.currentApplicationEvent.createdAt,
      videosCreated: influencer.stats.videos,
      videosViews: influencer.stats.views,
      earnings: influencer.stats.earnings,
      id: influencer.account.id,
      picture: influencer.account.picture,
      type: "influencer",
      removeInfluencer: () =>
        onApplicationAction({
          applicationType: "Rejected",
          applicant: influencer
        }),
      initChat: onInitChat
    }));

  const currentApplicants = applicants
    .filter(
      (applicant) => applicant.currentApplicationEvent.status === "Applied"
    )
    .map((applicant) => ({
      name: applicant.account.name,
      appliedDate: applicant.currentApplicationEvent.createdAt,
      videosCreated: applicant.stats.videos,
      videosViews: applicant.stats.views,
      earnings: applicant.stats.earnings,
      id: applicant.account.id,
      picture: applicant.account.picture,
      type: "applicant",
      approveApplicant: () =>
        onApplicationAction({
          applicationType: "Approved",
          applicant
        }),
      declineApplicant: () =>
        onApplicationAction({
          applicationType: "Rejected",
          applicant
        })
    }));

  return {
    applicants: currentApplicants,
    influencers
  };
};

export const onVisibilityChangeHof = ({
  opportunity,
  defaultIcon,
  setAlertState,
  updateOpportunity,
  refetch
}) => (visibility) => {
  if (opportunity.visibility === visibility) {
    return;
  }
  setAlertState({
    visible: true,
    icon: defaultIcon,
    intent: Intent.SUCCESS,
    confirmButtonText: `Make ${visibility}`,
    children:
      visibility === "Public" ? (
        <p>
          Are you sure you want to make this opportunity public? Anyone will be
          able to partner with "{opportunity.product.name}" without your
          approval.
        </p>
      ) : (
        <p>
          Are you sure you want to make this opportunity private? No one will be
          able to partner with "{opportunity.product.name}" until you approve.
        </p>
      ),
    onConfirm: async () => {
      await updateOpportunity({
        variables: {
          opportunity: {
            id: opportunity.id,
            visibility
          }
        }
      });
      await refetch();
      setAlertState({
        visible: false
      });
    }
  });
};

export const onDiscontinueHof = ({
  dangerIcon,
  opportunity,
  history,
  setAlertState,
  updateOpportunity
}) => async () => {
  setAlertState({
    visible: true,
    children: (
      <p>
        Are you sure you want to discontinue this opportunity? No one will be
        able to partner with it or buy products through this opportunity.
      </p>
    ),
    icon: dangerIcon,
    intent: Intent.DANGER,
    confirmButtonText: "Discontinue",
    onConfirm: async () => {
      await updateOpportunity({
        variables: {
          opportunity: {
            id: opportunity.id,
            isDiscontinued: true
          }
        }
      });
      history.push("/opportunities");
    }
  });
};

export const onInitChatHof = (setOtherUserId, { history }) => (userId) => {
  setOtherUserId(userId);
  history.push(`/chat`);
};

export const onExpirationExtendedHof = ({
  expirationDate,
  opportunity,
  refetch,
  setAlertState,
  defaultIcon,
  updateOpportunity
}) => () => {
  setAlertState({
    visible: true,
    icon: defaultIcon,
    intent: Intent.SUCCESS,
    confirmButtonText: `Extend Opportunity`,
    children: (
      <p>
        Are you sure you want to extend the opportunity expiration date? Current
        influencers will continue to be partnered with this opportunity until
        the new date has elapsed.
      </p>
    ),
    onConfirm: async () => {
      await updateOpportunity({
        variables: {
          opportunity: {
            id: opportunity.id,
            expiresAt: expirationDate.toUTCString()
          }
        }
      });
      await refetch();
      setAlertState({
        visible: false
      });
    }
  });
};

export const onApplicationActionHof = ({
  dangerIcon,
  successIcon,
  setAlertText,
  setAlertState,
  setApplicantId,
  setStatus
}) => ({ applicationType, applicant }) => {
  setApplicantId(applicant.account.id);
  setStatus(applicationType);
  setAlertState({
    visible: true,
    icon: applicationType === "Approved" ? successIcon : dangerIcon,
    intent: applicationType === "Approved" ? Intent.SUCCESS : Intent.DANGER,
    confirmButtonText: applicationType === "Approved" ? "Approve" : "Decline",
    children:
      applicationType === "Approved" ? (
        <div className={styles.textAreaParent}>
          <p>
            Approve {applicant.account.name} to partner with you on this
            opportunity?
          </p>
          <TextArea
            className={styles.textarea}
            fill
            placeholder={`(optional) Let ${applicant.account.name} know why you'd like to partner with them on this product.`}
            onChange={(event) => setAlertText(event.target.value)}
          />
        </div>
      ) : (
        <div className={styles.textAreaParent}>
          <p>
            Decline {applicant.account.name} request to partner with you on this
            opportunity?
          </p>
          <TextArea
            className={styles.textarea}
            fill
            placeholder={`(optional) Let ${applicant.account.name} know why you're rejecting their application.`}
            onChange={(event) => setAlertText(event.target.value)}
          />
        </div>
      )
  });
};
